import type { TFunction } from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/errors';
import { detailParams } from './reason-text';

/** The code shown (and copyable) for a failure that is not an API error, e.g. a crash in the page. */
export const CLIENT_ERROR_CODE = 'CLIENT_ERROR';

type Exists = (key: string) => boolean;

/**
 * Chooses the translated message for a failure. The rules, in order:
 *
 * 1. The server's structured sub-cause (`details.reason`), when translated,
 *    with the values the server sent beside it (`details.permissions`, `details.maxBytes`).
 * 2. The structured error code (`ORIGIN_NOT_ALLOWED`, `FORBIDDEN`, ...).
 * 3. Otherwise a generic message that carries the code, so it can be reported.
 *
 * Never the HTTP status alone, never the server's own (English) message.
 * Technical detail is logged to the console by the API client, not rendered.
 */
export function describeError(error: unknown, t: TFunction, exists: Exists): string {
  if (error instanceof ApiError) {
    const reasonKey = error.reason ? `errors.reason.${error.reason}` : null;
    if (reasonKey && exists(reasonKey)) return t(reasonKey, detailParams(error.details, t, exists));
    const codeKey = `errors.${error.code}`;
    if (exists(codeKey)) return t(codeKey);
    return t('errors.unknown', { code: error.code });
  }
  // A plain exception is a bug in the page, not something the person did.
  // eslint-disable-next-line no-console -- keep the stack for developers; the screen shows only the code.
  console.error(error);
  return t('errors.unknown', { code: CLIENT_ERROR_CODE });
}

/** Human message for any thrown error. See `describeError` for the rules. */
export function useErrorMessage(): (error: unknown) => string {
  const { t, i18n } = useTranslation();
  return useCallback((error: unknown) => describeError(error, t, (key) => i18n.exists(key)), [t, i18n]);
}
